/**
 * AssigneesEditor Component
 *
 * Multi-select editor for task assignees with search and add functionality.
 */

import { useState, useRef, useEffect, useMemo, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, User } from 'lucide-react';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useProfilesContext } from '@/context/ProfilesContext';
import { getInitials, getAvatarColor } from '@/utils/formatters';

export interface AssigneesEditorProps {
  assignees: string[];
  availableAssignees?: string[];
  /** Names of org members — assignees not in this set are flagged as external */
  orgMemberNames?: string[];
  /** Resolve a user_id to a display name (for UUID-based assignees) */
  resolveDisplayName?: (idOrName: string) => string;
  onAdd: (assignee: string) => void;
  onRemove: (assignee: string) => void;
  size?: 'sm' | 'md';
  maxDisplay?: number;
  disabled?: boolean;
}

export function AssigneesEditor({
  assignees,
  availableAssignees = [],
  orgMemberNames,
  resolveDisplayName: resolveDisplayNameProp,
  onAdd,
  onRemove,
  size = 'md',
  maxDisplay = 3,
  disabled = false,
}: AssigneesEditorProps) {
  const { resolveDisplayName: resolveFromContext, loaded: profilesLoaded } = useProfilesContext();
  const resolveDisplayName = resolveDisplayNameProp || resolveFromContext;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 220,
    matchTriggerWidth: false,
  });

  useClickOutside(
    [dropdownRef, triggerRef],
    () => {
      setIsOpen(false);
      setSearchTerm('');
    },
    isOpen
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const orgMemberSet = useMemo(
    () => orgMemberNames ? new Set(orgMemberNames.map(n => n.toLowerCase())) : null,
    [orgMemberNames]
  );
  const isOrgMember = (name: string) => !orgMemberSet || orgMemberSet.has(name.toLowerCase());

  const filteredAssignees = availableAssignees.filter(
    (a) => !assignees.includes(a) && (
      a.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resolveDisplayName(a).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleAddNew = () => {
    if (searchTerm.trim() && !assignees.includes(searchTerm.trim())) {
      onAdd(searchTerm.trim());
      setSearchTerm('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredAssignees.length > 0) {
        onAdd(filteredAssignees[0]);
        setSearchTerm('');
      } else {
        handleAddNew();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const avatarSize = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10px]';
  const containerHeight = size === 'sm' ? 'h-6' : 'h-7';

  return (
    <div ref={triggerRef} className="relative inline-flex items-center">
      {/* Container with fixed height and background */}
      <div
        className={clsx(
          'flex items-center gap-0.5 px-1 rounded-md bg-surface-tertiary',
          containerHeight
        )}
      >
        {/* Displayed Assignees */}
        <div className="flex items-center -space-x-1.5">
          {assignees.slice(0, maxDisplay).map((assignee) => {
            const displayName = resolveDisplayName(assignee);
            const isUuid = /^[0-9a-f]{8}-/.test(assignee);
            const showSkeleton = !profilesLoaded && isUuid && displayName === assignee;
            return (
            <div
              key={assignee}
              className={clsx(
                'rounded-full flex items-center justify-center font-semibold text-white border-2 border-surface-tertiary relative group',
                avatarSize,
                showSkeleton ? 'bg-surface-hover animate-pulse' : getAvatarColor(displayName),
                !disabled && 'cursor-pointer'
              )}
              title={showSkeleton ? '' : (isOrgMember(assignee) ? displayName : `${displayName} (esterno)`)}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onRemove(assignee);
              }}
            >
              {showSkeleton ? '' : getInitials(displayName)}
              {!disabled && !showSkeleton && (
                <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <X className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          );
          })}
          {assignees.length > maxDisplay && (
            <div
              className={clsx(
                'rounded-full bg-surface-hover flex items-center justify-center font-semibold text-text-tertiary border-2 border-surface-tertiary',
                avatarSize
              )}
            >
              +{assignees.length - maxDisplay}
            </div>
          )}
        </div>

        {/* Add Button */}
        {!disabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className={clsx(
              'rounded-full flex items-center justify-center hover:bg-surface-hover transition-all shrink-0',
              avatarSize
            )}
            title="Aggiungi assegnatario"
          >
            <Plus className="w-3 h-3 text-text-quaternary" />
          </button>
        )}

        {/* Placeholder when no assignees */}
        {assignees.length === 0 && disabled && (
          <div className="flex items-center gap-1 text-text-quaternary">
            <User className={clsx(size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
            <span className={clsx(size === 'sm' ? 'text-[10px]' : 'text-[11px]')}>—</span>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-lg shadow-2xl min-w-[200px] animate-fade-in border border-border-primary/70 bg-surface-primary"
          >
            {/* Search Input */}
            <div className="p-2 border-b border-white/10">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Cerca o aggiungi..."
                className="w-full px-2 py-1.5 text-[11px] bg-black/30 border border-white/10 rounded-md focus:outline-none focus:border-accent-violet text-text-primary placeholder-text-quaternary"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options */}
            <div className="max-h-[200px] overflow-y-auto py-1">
              {filteredAssignees.map((assignee) => {
                const dn = resolveDisplayName(assignee);
                return (
                <button
                  key={assignee}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAdd(assignee);
                    setSearchTerm('');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10 transition-colors"
                >
                  <div
                    className={clsx(
                      'w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white',
                      getAvatarColor(dn)
                    )}
                  >
                    {getInitials(dn)}
                  </div>
                  <span className="text-[11px] text-text-primary truncate">{dn}</span>
                  {!isOrgMember(assignee) && (
                    <span className="ml-auto text-[9px] text-amber-400/80 shrink-0" title="Non è membro dell'organizzazione">
                      esterno
                    </span>
                  )}
                </button>
              );
              })}
              {searchTerm &&
                !filteredAssignees.some((a) => a.toLowerCase() === searchTerm.toLowerCase()) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddNew();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10 transition-colors text-accent-violet"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-[11px]">Aggiungi "{searchTerm}"</span>
                  </button>
                )}
              {filteredAssignees.length === 0 && !searchTerm && (
                <div className="px-3 py-2 text-[11px] text-text-quaternary text-center">
                  Nessun suggerimento
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
